// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import {
  Box,
  Button,
  CircularProgress,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'

// **Component Imports
import CustomDialog from '../../../../../components/dialog'
import Icon from '../../../../../components/icon'
import QuotaTooltip from '../../../../../components/quota-tooltip'
import CustomTextField from '../../../../../components/textfield'

// **API Imports
import { getFeatureQuota } from '../../../../../axios/api/contest'

// **Utils Imports
import { RE_ENABLE_ATTEMPT_MODE } from '../../../../../utils/constant'
import {
  rgbaToHexWithoutOpacity,
  showErrorToast
} from '../../../../../utils/utils'

// ** Warning Message Component
const WarningMessage = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      p: '10px 14px',
      borderRadius: '8px',
      backgroundColor: rgbaToHexWithoutOpacity(255, 251, 235, 1),
      color: 'warning.dark',
      border: '1px solid',
      borderColor: 'warning.main'
    }}
  >
    <Icon
      icon="mdi:alert-outline"
      fontSize={20}
      style={{ marginTop: 1, flexShrink: 0 }}
    />
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: 'inherit', lineHeight: 1.5 }}
    >
      {message}
    </Typography>
  </Box>
)

// ** Radio Card Component
const RadioCard = ({
  value,
  selected,
  label,
  description,
  onChange,
  disabled = false
}) => (
  <Box
    {...(disabled ? {} : { onClick: () => onChange(value) })}
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      p: '10px 12px',
      borderRadius: '8px',
      border: '1.5px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      backgroundColor: selected ? 'primary.lighter' : 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: disabled
          ? 'divider'
          : selected
            ? 'primary.main'
            : 'action.disabled',
        backgroundColor: disabled
          ? 'transparent'
          : selected
            ? 'primary.lighter'
            : 'action.hover'
      }
    }}
  >
    <Radio
      size="small"
      checked={selected}
      value={value}
      sx={{ p: 0, mt: '2px' }}
      disabled={disabled}
    />
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          lineHeight: 1.4,
          display: 'block',
          mt: 0.25
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
)

const ReEnableAttemptModal = ({
  open,
  data = null,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isAssessment = true
}) => {
  // State
  const [loading, setLoading] = useState(false)
  const [quotaDetails, setQuotaDetails] = useState(null)
  const [formState, setFormState] = useState({
    minutes: '',
    error: '',
    attemptMode: ''
  })
  // Variables
  const isAIScreening = useMemo(() => {
    return data?.isAIScreening || false
  }, [data?.isAIScreening])

  const name = useMemo(() => {
    return data?.name || ''
  }, [data?.name])

  // Handlers
  const fetchQuotaDetails = useCallback(async (stageId, candidateId) => {
    setLoading(true)
    const response = await getFeatureQuota(stageId, 1, candidateId)
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setQuotaDetails(response)
    }
  }, [])

  const handleChange = useCallback((key, value) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'minutes' ? { error: '' } : {}),
      ...(key === 'attemptMode' ? { minutes: '', error: '' } : {})
    }))
  }, [])

  const handleConfirm = useCallback(() => {
    if (!formState?.attemptMode) {
      return
    }

    if (
      !isAIScreening &&
      isAssessment &&
      formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.CONTINUE
    ) {
      const parsedMinutes = parseInt(formState?.minutes)
      if (!formState?.minutes) {
        setFormState((prev) => ({
          ...prev,
          error: 'Duration is required'
        }))
        return
      }

      if (isNaN(parsedMinutes) || parsedMinutes <= 0) {
        setFormState((prev) => ({
          ...prev,
          error: 'Please enter a valid number of minutes'
        }))
        return
      }
      if (data?.duration && parsedMinutes > data?.duration) {
        setFormState((prev) => ({
          ...prev,
          error: `Duration cannot exceed stage duration (${data?.duration} minutes).`
        }))
        return
      }
      onSubmit(formState?.attemptMode, parsedMinutes)
    } else {
      onSubmit(formState?.attemptMode, data?.duration)
    }
  }, [
    isAIScreening,
    formState?.attemptMode,
    formState?.minutes,
    onSubmit,
    data?.duration,
    isAssessment
  ])
  const renderMessage = useMemo(() => {
    if (formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.RESTART) {
      return 'Selecting Fresh Start will permanently delete all code files, session recordings, auxiliary evidence, and remarks. A new attempt will begin with the full stage duration.'
    }

    if (formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.CONTINUE) {
      return 'The attempt will resume from the last saved progress and the timeline will be adjusted accordingly.'
    }

    return ''
  }, [formState?.attemptMode])

  const disabledButton = useMemo(() => {
    return isSubmitting
      ? true
      : quotaDetails?.newCredits < 0
        ? true
        : isAIScreening
          ? false
          : !formState?.attemptMode
            ? true
            : false
  }, [
    isAIScreening,
    isSubmitting,
    formState?.attemptMode,
    quotaDetails?.newCredits
  ])

  // Effects
  useEffect(() => {
    if (open && data?.stageId && data?.candidateId) {
      fetchQuotaDetails(data?.stageId, data?.candidateId)
    }

    if (!open) {
      setQuotaDetails(null)
      setFormState({ minutes: '', error: '', attemptMode: '' })
      setLoading(false)
    }
  }, [open, data?.stageId, data?.candidateId, fetchQuotaDetails])

  useEffect(() => {
    if (open && isAIScreening) {
      setFormState({
        minutes: '',
        error: '',
        attemptMode: RE_ENABLE_ATTEMPT_MODE.RESTART
      })
    }
  }, [open, isAIScreening])

  return (
    <CustomDialog
      open={open}
      outsideClikClose={false}
      handleClose={handleClose}
      title={`Re-enable Attempt ${name ? `for ${name}` : ''}`}
      dialogPaperStyle={{
        maxWidth: '520px',
        width: '45%',
        '& .MuiDialogContent-root': {
          padding: '0px !important'
        }
      }}
      renderHeader={() => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuotaTooltip
            data={quotaDetails}
            placement="bottom"
            loading={loading}
          />
        </Box>
      )}
    >
      <Box sx={{ padding: '12px 24px 0px 24px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          {isAssessment &&
            formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.CONTINUE && (
              <Box>
                <CustomTextField
                  required
                  fullWidth
                  type="number"
                  label="Duration"
                  placeholder="Enter duration in minutes"
                  value={formState?.minutes}
                  onChange={(e) => handleChange('minutes', e?.target?.value)}
                  error={Boolean(formState?.error)}
                  helperText={formState?.error}
                />
              </Box>
            )}

          {/* Attempt Mode */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                mb: 1,
                lineHeight: 1.154,
                color: 'text.primary'
              }}
            >
              Attempt Mode
            </Typography>
            <RadioGroup value={formState?.attemptMode}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <RadioCard
                  value={RE_ENABLE_ATTEMPT_MODE.CONTINUE}
                  selected={
                    formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.CONTINUE
                  }
                  label="Continue"
                  description="Continue from the last saved progress"
                  onChange={(val) => handleChange('attemptMode', val)}
                  disabled={isAIScreening}
                />
                <RadioCard
                  value={RE_ENABLE_ATTEMPT_MODE.RESTART}
                  selected={
                    formState?.attemptMode === RE_ENABLE_ATTEMPT_MODE.RESTART
                  }
                  label="Fresh Start"
                  description="Reset previous attempt and start a new attempt"
                  onChange={(val) => handleChange('attemptMode', val)}
                />
              </Box>
            </RadioGroup>
          </Box>

          {formState?.attemptMode && <WarningMessage message={renderMessage} />}
        </Box>

        {/* {loading ? (
          <Skeleton height={40} width={'100%'} animation="wave" />
        ) : (
          <QuotaWidget data={quotaDetails} />
        )} */}
      </Box>

      <Box sx={{ padding: '16px 24px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2
          }}
        >
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={disabledButton}
          >
            {isSubmitting ? <CircularProgress size={22} /> : 'Confirm'}
          </Button>
        </Box>
      </Box>
    </CustomDialog>
  )
}

export default memo(ReEnableAttemptModal)
